// Modal that asks the user to choose a unique in-game name. Used in two
// places: first-run after the language Continue (mode='register') and the
// Account sub-panel of Settings (mode='rename'). Both flows resolve a
// validated, server-accepted name and persist via the appropriate API.
//
// Visually mirrors SettingsScreen — pale-blue gradient card on a darkened
// scrim, white pill rows, primary-blue gradient action button. Self-contained
// styles so it doesn't depend on Settings being open.

import { MenuNav, type MenuItem } from './MenuNav';
import { menuAudio } from './MenuAudio';
import { StageScaler } from './StageScaler';
import { accountApi, ApiError } from '../net/accountApi';
import { t } from '../i18n';

const STYLES_ID = 'name-picker-styles';
const CARD_W = 720;
const CARD_H = 560;

export type NamePickerMode = 'register' | 'rename';

export interface NamePickerOptions {
  mode: NamePickerMode;
  initialName?: string;
  /** Called once the name has been registered/renamed and persisted server-side. */
  onSubmit: (name: string) => Promise<void> | void;
  /** Optional dismiss in rename mode. Register mode does not dismiss without
   *  a successful submit (the user must pick a name to proceed). */
  onCancel?: () => void;
}

export interface NamePickerHandle {
  dismiss(): void;
}

export function showNamePickerModal(opts: NamePickerOptions): NamePickerHandle {
  ensureStyles();

  const allowCancel = opts.mode === 'rename' && typeof opts.onCancel === 'function';

  const root = document.createElement('div');
  root.className = 'name-picker-modal';
  root.innerHTML = `
    <div class="np-backdrop"></div>
    <div class="np-stage-host">
      <div class="np-card">
        <div class="np-header">
          <div class="np-title">${escapeHtml(t(opts.mode === 'register' ? 'name.titleRegister' : 'name.titleRename'))}</div>
        </div>
        <div class="np-body">
          <div class="np-blurb">${escapeHtml(t('name.blurb'))}</div>
          <div class="np-row">
            <input type="text" class="np-input" maxlength="16" autocomplete="off"
                   spellcheck="false" autocapitalize="off" />
          </div>
          <div class="np-status" data-state="idle"></div>
          <div class="np-actions">
            ${allowCancel ? `<button type="button" class="np-btn np-btn-secondary np-cancel">${escapeHtml(t('name.cancel'))}</button>` : ''}
            <button type="button" class="np-btn np-btn-primary np-submit" disabled>${escapeHtml(t(opts.mode === 'register' ? 'name.continue' : 'name.save'))}</button>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(root);
  requestAnimationFrame(() => root.classList.add('shown'));

  const cardEl = root.querySelector('.np-card') as HTMLElement;
  const input = root.querySelector('.np-input') as HTMLInputElement;
  const statusEl = root.querySelector('.np-status') as HTMLElement;
  const submitBtn = root.querySelector('.np-submit') as HTMLButtonElement;
  const cancelBtn = root.querySelector('.np-cancel') as HTMLButtonElement | null;

  cardEl.style.width = `${CARD_W}px`;
  cardEl.style.height = `${CARD_H}px`;
  const scaler = new StageScaler({ stageEl: cardEl, designW: CARD_W, designH: CARD_H, maxScale: 1.0 });
  scaler.start();

  if (opts.initialName) input.value = opts.initialName.slice(0, 16);

  let dismissed = false;
  let nav: MenuNav | null = null;
  let busy = false;
  let lastChecked = '';
  let debounceTimer = 0;
  let availSeq = 0;

  function setStatus(state: 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'error', message?: string) {
    statusEl.dataset.state = state;
    statusEl.textContent = message ?? '';
  }

  function clientValidate(name: string): null | 'short' | 'long' | 'format' {
    if (name.length === 0) return null;
    if (name.length < 3) return 'short';
    if (name.length > 16) return 'long';
    if (!/^[a-zA-Z0-9_-]+$/.test(name)) return 'format';
    return null;
  }

  function refreshSubmitState(canSubmit: boolean) {
    submitBtn.disabled = !canSubmit || busy;
    submitBtn.classList.toggle('np-btn-disabled', submitBtn.disabled);
  }

  async function checkAvailability(name: string) {
    if (name === lastChecked) return;
    lastChecked = name;
    const seq = ++availSeq;
    setStatus('checking', t('name.statusChecking'));
    refreshSubmitState(false);
    try {
      const r = await accountApi.checkName(name);
      if (seq !== availSeq) return;
      if (r.available) {
        setStatus('available', t('name.statusAvailable'));
        refreshSubmitState(true);
      } else {
        const reasonKey =
          r.reason === 'taken' ? 'name.errorTaken' :
          r.reason === 'profanity' ? 'name.errorProfanity' :
          r.reason === 'reserved' ? 'name.errorReserved' :
          'name.errorFormat';
        setStatus('taken', t(reasonKey));
        refreshSubmitState(false);
      }
    } catch (e) {
      if (seq !== availSeq) return;
      setStatus('error', t('name.statusNetworkError'));
      refreshSubmitState(false);
    }
  }

  input.addEventListener('input', () => {
    const raw = input.value.trim();
    const issue = clientValidate(raw);
    if (issue === 'short') {
      setStatus('idle', t('name.hintMinLength'));
      refreshSubmitState(false);
    } else if (issue === 'long') {
      setStatus('invalid', t('name.errorTooLong'));
      refreshSubmitState(false);
    } else if (issue === 'format') {
      setStatus('invalid', t('name.errorFormat'));
      refreshSubmitState(false);
    } else if (raw.length === 0) {
      setStatus('idle', '');
      refreshSubmitState(false);
    } else {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => checkAvailability(raw), 280);
    }
  });

  async function attemptSubmit() {
    if (busy) return;
    const name = input.value.trim();
    const issue = clientValidate(name);
    if (issue || name.length < 3) return;
    busy = true;
    refreshSubmitState(false);
    setStatus('checking', t('name.statusSubmitting'));
    try {
      menuAudio.playClick?.();
    } catch {}
    try {
      await opts.onSubmit(name);
      dismiss();
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      const key =
        code === 'name_taken' ? 'name.errorTaken' :
        code === 'name_profanity' ? 'name.errorProfanity' :
        code === 'name_reserved' ? 'name.errorReserved' :
        code === 'name_format' ? 'name.errorFormat' :
        code === 'rate_limited' ? 'name.errorRateLimited' :
        'name.statusNetworkError';
      setStatus('error', t(key));
      busy = false;
      refreshSubmitState(false);
    }
  }

  submitBtn.addEventListener('click', attemptSubmit);
  cancelBtn?.addEventListener('click', () => {
    if (busy) return;
    opts.onCancel?.();
    dismiss();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      attemptSubmit();
    }
  });

  function onKeyDown(e: KeyboardEvent) {
    if (dismissed) return;
    if (e.key === 'Escape' && allowCancel) {
      e.preventDefault();
      e.stopPropagation();
      opts.onCancel?.();
      dismiss();
    }
  }
  document.addEventListener('keydown', onKeyDown, { capture: true });

  // MenuNav so gamepad/keyboard nav reaches the buttons. The text input is
  // mouse/keyboard-only; gamepad users can still activate Submit / Cancel.
  const items: MenuItem[] = [];
  if (cancelBtn) items.push({ el: cancelBtn, row: 0, col: 0, onActivate: () => cancelBtn.click() });
  items.push({ el: submitBtn, row: 0, col: items.length, onActivate: attemptSubmit });
  nav = new MenuNav({ items, initialIndex: items.length - 1 });
  nav.mount();

  // Focus the text field so the user can type immediately.
  setTimeout(() => input.focus({ preventScroll: true }), 50);

  function dismiss() {
    if (dismissed) return;
    dismissed = true;
    nav?.dispose();
    nav = null;
    scaler.stop();
    document.removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    window.clearTimeout(debounceTimer);
    root.classList.add('closing');
    setTimeout(() => root.remove(), 240);
  }

  return { dismiss };
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

const STYLES = `
.name-picker-modal {
  position: fixed; inset: 0; z-index: 115;
  pointer-events: auto;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
}
.name-picker-modal .np-backdrop {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  opacity: 0;
  transition: opacity 200ms ease-out;
}
.name-picker-modal.shown .np-backdrop { opacity: 1; }
.name-picker-modal.closing .np-backdrop { opacity: 0; transition-duration: 200ms; }
.name-picker-modal .np-stage-host { position: absolute; inset: 0; pointer-events: none; }
.name-picker-modal .np-card {
  position: absolute; top: 50%; left: 50%;
  pointer-events: auto;
  background: linear-gradient(180deg, #C7E6F1 0%, #B8DEEC 100%);
  border-radius: 28px;
  box-shadow: 0 16px 24px rgba(0,0,0,0.22), inset 0 -4px 6px rgba(89,168,192,0.4);
  display: flex; flex-direction: column;
  overflow: hidden;
  opacity: 0;
  transition: opacity 240ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.name-picker-modal.shown .np-card { opacity: 1; }
.name-picker-modal.closing .np-card { opacity: 0; transition-duration: 200ms; }

.name-picker-modal .np-header { padding: 28px 36px 8px 36px; }
.name-picker-modal .np-title {
  font-size: 44px; line-height: 1;
  color: #0E3C4A;
  letter-spacing: 0.04em;
}
.name-picker-modal .np-body {
  flex: 1;
  padding: 12px 36px 28px 36px;
  display: flex; flex-direction: column; gap: 18px;
}
.name-picker-modal .np-blurb {
  font-size: 20px; line-height: 1.35;
  color: #5C8090;
  letter-spacing: 0.02em;
}
.name-picker-modal .np-row {
  background: #FFFFFF;
  border-radius: 18px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -3px 0 rgba(176,209,219,0.6);
  padding: 14px 22px;
}
.name-picker-modal .np-input {
  width: 100%;
  border: 0; outline: 0;
  background: transparent;
  font-family: 'Chewy', sans-serif;
  font-size: 36px; line-height: 1;
  color: #0E3C4A;
  letter-spacing: 0.04em;
  caret-color: #2A6DA0;
}
.name-picker-modal .np-input::placeholder { color: #9CC0CC; }
.name-picker-modal .np-status {
  min-height: 28px;
  font-size: 20px; line-height: 1.2;
  letter-spacing: 0.02em;
  padding: 0 6px;
}
.name-picker-modal .np-status[data-state="idle"]      { color: #5C8090; }
.name-picker-modal .np-status[data-state="checking"]  { color: #5C8090; font-style: italic; }
.name-picker-modal .np-status[data-state="available"] { color: #2A8F4D; }
.name-picker-modal .np-status[data-state="taken"]     { color: #C24A2C; }
.name-picker-modal .np-status[data-state="invalid"]   { color: #C24A2C; }
.name-picker-modal .np-status[data-state="error"]     { color: #C24A2C; }

.name-picker-modal .np-actions {
  display: flex; gap: 14px; justify-content: flex-end;
  margin-top: auto;
}
.name-picker-modal .np-btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 28px; line-height: 1;
  padding: 16px 26px;
  border-radius: 18px;
  letter-spacing: 0.04em;
  color: #FFFFFF;
  transition: filter 120ms ease, transform 120ms ease, box-shadow 120ms ease;
}
.name-picker-modal .np-btn-primary {
  background: linear-gradient(180deg, #6FCEEB 0%, #3DA6FF 100%);
  -webkit-text-stroke: 2px #2A6DA0;
  paint-order: stroke fill;
  box-shadow: 0 4px 0 #2C7AB6, 0 6px 14px rgba(0,0,0,0.2);
}
.name-picker-modal .np-btn-primary:active { transform: translateY(3px); box-shadow: 0 1px 0 #2C7AB6; }
.name-picker-modal .np-btn-primary:hover,
.name-picker-modal .np-btn-primary.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 3px; }
.name-picker-modal .np-btn-secondary {
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  color: #0E3C4A;
  -webkit-text-stroke: 0;
  box-shadow: 0 4px 0 #ADC5CC;
}
.name-picker-modal .np-btn-secondary:active { transform: translateY(3px); box-shadow: 0 1px 0 #ADC5CC; }
.name-picker-modal .np-btn-secondary:hover,
.name-picker-modal .np-btn-secondary.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.7); outline-offset: 3px; }
.name-picker-modal .np-btn-disabled,
.name-picker-modal .np-btn[disabled] {
  filter: grayscale(0.4) brightness(0.92);
  cursor: not-allowed;
  pointer-events: none;
}
`;
